import React from 'react';
import { KfsInputs } from '../types/kfs.types';
import { ValidationErrors } from '../hooks/useKfsCalculator';
import IconSystem from 'icon-collection/icon-systems';

interface KfsInputFormProps {
  inputs: KfsInputs;
  onChange: (inputs: KfsInputs) => void;
  errors: ValidationErrors;
}

function toDateInputValue(date: Date | null): string {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const KfsInputForm: React.FC<KfsInputFormProps> = ({ inputs, onChange, errors }) => {
  const handleNumberChange = (field: keyof KfsInputs) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange({ ...inputs, [field]: val === '' ? null : parseFloat(val) });
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange({ ...inputs, loanDate: val ? new Date(val + 'T00:00:00') : null });
  };

  return (
    <div className="kfs-card">
      <div className="kfs-card-header">
        <div className="kfs-card-header-icon blue">
          <IconSystem.edit size={16} />
        </div>
        <h3>Loan Details</h3>
        <span>Enter all 4 fields to calculate</span>
      </div>
      <div className="kfs-card-body">
        <div className="kfs-input-grid">
          <div className="form-group">
            <label className="form-label">Loan Amount (₹)</label>
            <input
              className={`form-input ${errors.loanAmount ? 'form-input-error' : ''}`}
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g. 100000"
              value={inputs.loanAmount ?? ''}
              onChange={handleNumberChange('loanAmount')}
            />
            {errors.loanAmount && <span className="kfs-field-error">{errors.loanAmount}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Loan Date</label>
            <input
              className={`form-input ${errors.loanDate ? 'form-input-error' : ''}`}
              type="date"
              value={toDateInputValue(inputs.loanDate)}
              onChange={handleDateChange}
            />
            {errors.loanDate && <span className="kfs-field-error">{errors.loanDate}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Interest Rate (%)</label>
            <input
              className={`form-input ${errors.interestRate ? 'form-input-error' : ''}`}
              type="number"
              min="0"
              max="100"
              step="0.001"
              placeholder="e.g. 12"
              value={inputs.interestRate ?? ''}
              onChange={handleNumberChange('interestRate')}
            />
            {errors.interestRate && <span className="kfs-field-error">{errors.interestRate}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Gross Weight (g)</label>
            <input
              className={`form-input ${errors.grossWeight ? 'form-input-error' : ''}`}
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g. 14.58"
              value={inputs.grossWeight ?? ''}
              onChange={handleNumberChange('grossWeight')}
            />
            {errors.grossWeight && <span className="kfs-field-error">{errors.grossWeight}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KfsInputForm;
